/**
 * ActionCard — BitDrop file transfer card.
 *
 * Thin coordinator: owns tab state and delegates rendering to focused molecules.
 * Follows WeTransfer's world-class minimalist aesthetic:
 *   - 360px floating card with 24px border radius and crisp monochrome border
 *   - In-Card Segmented Switcher [ Send ] & [ Receive ]
 *   - Empty Dropzone with 44px circular [+] button, bold title, subtitle, and "+ Add folders"
 *   - Integrated FileList when files are staged
 *   - Title (optional) & Add a message (optional) inputs
 *   - Expiry selector pill + options button
 *   - Primary tactile CTA button
 */
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Modal,
  TouchableWithoutFeedback,
} from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { Colors, Shadows, Spacing, Radius, FontSize, FontWeight, Layout } from '../theme/tokens';
import { Icon, IconName } from '../atoms/Icon';
import { CircularProgress } from '../molecules/CircularProgress';
import { FileList, fmtSize } from '../molecules/FileList';
import { ShareResult } from '../molecules/ShareResult';
import { AppConfig } from '../../../shared/config/app-config';
import { readFromClipboard } from '../../../shared/utils/clipboard';

const EXPIRY_PRESETS = [
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
  { value: 'never', label: 'Never' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ActionCardProps {
  activeTab:          'send' | 'receive';
  onTabChange:        (tab: string) => void;
  isSending:          boolean;
  /** Triggers the transfer (used for the Transfer button and initial "Add files"). */
  onSendFileClick:    () => void;
  /** Opens folder picker only. */
  onSendFolderClick?: () => void;
  /**
   * Opens file picker to ADD more files to the existing staged set.
   * Wired to openFilePicker(), NOT handleTransfer().
   */
  onAddMoreFiles?:    () => void;
  joinCode:           string;
  onJoinCodeChange:   (t: string) => void;
  onStartDownload:    () => void;
  onOpenQRScanner:    () => void;
  onPasteLink?:       () => void;
  selectedFiles?:     File[];
  onRemoveFile?:      (i: number) => void;
  // Transfer lifecycle
  isPreparing?:       boolean;
  prepareProgress?:   number;
  shareUrl?:          string | null;
  shareHash?:         string | null;
  torrentName?:       string;
  torrentFile?:       Uint8Array;
  onCopy?:            (url: string) => void;
  onCloseShare?:      () => void;
  onViewDetails?:     () => void;
  selectedExpiry?:    string;
  onExpiryChange?:    (expiry: string) => void;
}

// ─── Field Input Component ───────────────────────────────────────────────────

interface FieldProps {
  value?:        string;
  onChange?:     (t: string) => void;
  placeholder?:  string;
  right?:        React.ReactNode;
  multiline?:    boolean;
  keyboardType?: any;
  borderColor:   string;
  textColor:     string;
  labelColor:    string;
}

const Field: React.FC<FieldProps> = ({
  value,
  onChange,
  placeholder,
  right,
  multiline,
  keyboardType,
  borderColor,
  textColor,
  labelColor,
}) => (
  <View style={[fieldStyles.wrap, { borderBottomColor: borderColor }]}>
    <View style={fieldStyles.row}>
      <TextInput
        style={[
          fieldStyles.input,
          { color: textColor },
          multiline && fieldStyles.multiline,
        ] as any}
        value={value}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor={labelColor}
        multiline={multiline}
        numberOfLines={multiline ? 3 : 1}
        keyboardType={keyboardType}
        autoCapitalize="none"
        autoCorrect={false}
      />
      {right && <View style={fieldStyles.right}>{right}</View>}
    </View>
  </View>
);

const fieldStyles = StyleSheet.create({
  wrap: {
    borderBottomWidth: 1,
    paddingVertical: 10,
    marginTop: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  input: {
    fontSize: FontSize.sm + 1,
    outlineStyle: 'none',
    flex: 1,
    fontWeight: FontWeight.regular,
    letterSpacing: -0.1,
  } as any,
  multiline: {
    minHeight: 48,
    textAlignVertical: 'top',
    marginTop: 2,
  },
  right: {
    marginLeft: 10,
    marginTop: 2,
  },
});

// ─── Upload / Quick Action Button ────────────────────────────────────────────

interface UploadBtnProps {
  label: string;
  iconName: IconName;
  bg: string;
  textColor: string;
  onPress: () => void;
}

const UploadBtn: React.FC<UploadBtnProps> = ({
  label,
  iconName,
  bg,
  textColor,
  onPress,
}) => {
  const { colors } = useTheme();
  return (
    <TouchableOpacity
      style={[ubStyles.btn, { backgroundColor: bg, borderColor: colors.border }]}
      onPress={onPress}
      activeOpacity={0.8}
    >
      <View style={[ubStyles.circle, { backgroundColor: colors.surface }]}>
        <Icon name={iconName} size={16} color={colors.text as string} strokeWidth={2} />
      </View>
      <Text style={[ubStyles.label, { color: textColor }]}>{label}</Text>
    </TouchableOpacity>
  );
};

const ubStyles = StyleSheet.create({
  btn: {
    flex: 1,
    borderRadius: Radius.lg,
    padding: Spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 88,
    borderWidth: 1,
  },
  circle: {
    width: 32,
    height: 32,
    borderRadius: Radius.full,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  label: {
    fontSize: FontSize.xs + 1,
    fontWeight: FontWeight.semibold,
    letterSpacing: -0.1,
  },
});

const QuickActionBtn = UploadBtn;

// ─── Main ActionCard Component ───────────────────────────────────────────────

export const ActionCard: React.FC<ActionCardProps> = ({
  activeTab,
  onTabChange,
  isSending,
  onSendFileClick,
  onSendFolderClick,
  onAddMoreFiles,
  joinCode,
  onJoinCodeChange,
  onStartDownload,
  onOpenQRScanner,
  onPasteLink,
  selectedFiles = [],
  onRemoveFile,
  isPreparing,
  prepareProgress,
  shareUrl,
  shareHash,
  torrentName,
  torrentFile,
  onCopy,
  onCloseShare,
  onViewDetails,
  selectedExpiry = '3d',
  onExpiryChange,
}) => {
  const { colors } = useTheme();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [expiryModalVisible, setExpiryModalVisible] = useState(false);

  const cardBg = colors.surface;
  const uploadAreaBg = colors.pill;
  const isSendTab = activeTab === 'send';
  const hasStagedFiles = selectedFiles.length > 0;

  return (
    <View style={[styles.card, { backgroundColor: cardBg, borderColor: colors.border }]}>
      {/* ── Send Empty State: Add Files & Add Folders ───────────────── */}
      {isSendTab && !hasStagedFiles && !isPreparing && !shareUrl && (
        <View style={styles.uploadRow as any}>
          <UploadBtn
            label="Add files"
            iconName="plus"
            bg={uploadAreaBg as string}
            textColor={colors.text as string}
            onPress={onSendFileClick}
          />
          <UploadBtn
            label="Add folders"
            iconName="folder"
            bg={uploadAreaBg as string}
            textColor={colors.text as string}
            onPress={onSendFolderClick ?? onSendFileClick}
          />
        </View>
      )}

      {/* ── Staged File List (with type icons, sizes & add-more) ─────── */}
      {isSendTab && hasStagedFiles && !isPreparing && !shareUrl && (
        <FileList
          files={selectedFiles}
          onAddMoreFiles={onAddMoreFiles ?? onSendFileClick}
          onRemoveFile={onRemoveFile ?? (() => {})}
        />
      )}

      {/* ── Direct P2P Badge (Send mode) ────────────────────────────── */}
      {isSendTab && !isPreparing && !shareUrl && (
        <View style={styles.upsellRow}>
          <Icon name="zap" size={12} color={colors.brand as string} strokeWidth={2} />
          <Text style={[styles.upsellText, { color: colors.subtext, marginLeft: 6 }]}>
            Unlimited size & speed · Direct P2P
          </Text>
        </View>
      )}

      {/* ── Optional Title & Message Fields (Send mode) ─────────────── */}
      {isSendTab && !isPreparing && !shareUrl && (
        <View style={styles.fieldsContainer}>
          <Field
            placeholder="Title (optional)"
            value={title}
            onChange={setTitle}
            borderColor={colors.border as string}
            textColor={colors.text as string}
            labelColor={colors.subtext as string}
          />
          <Field
            placeholder="Add a message (optional)"
            value={message}
            onChange={setMessage}
            multiline
            borderColor={colors.border as string}
            textColor={colors.text as string}
            labelColor={colors.subtext as string}
          />
        </View>
      )}

      {/* ── Receive Empty Quick Actions ─────────────────────────────── */}
      {!isSendTab && !joinCode && !isPreparing && !shareUrl && (
        <View style={styles.quickActionRow}>
          <QuickActionBtn
            label="Paste link"
            iconName="link"
            bg={uploadAreaBg as string}
            textColor={colors.text as string}
            onPress={async () => {
              if (onPasteLink) {
                onPasteLink();
              } else {
                const text = await readFromClipboard();
                if (text) onJoinCodeChange(text.trim());
              }
            }}
          />
          <QuickActionBtn
            label="Scan QR code"
            iconName="camera"
            bg={uploadAreaBg as string}
            textColor={colors.text as string}
            onPress={onOpenQRScanner}
          />
        </View>
      )}

      {/* ── Receive Input Field ──────────────────────────────────────── */}
      {!isSendTab && !isPreparing && !shareUrl && (
        <View style={styles.receiveSection}>
          <Field
            value={joinCode}
            onChange={onJoinCodeChange}
            placeholder={`${AppConfig.shareBaseUrl}/xxx or magnet:...`}
            right={
              joinCode ? (
                <TouchableOpacity
                  onPress={() => onJoinCodeChange('')}
                  style={styles.fieldActionBtn}
                  activeOpacity={0.7}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Icon name="close" size={16} color={colors.subtext as string} strokeWidth={2} />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  onPress={onOpenQRScanner}
                  style={styles.fieldActionBtn}
                  activeOpacity={0.7}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Icon name="camera" size={17} color={colors.brand as string} strokeWidth={2} />
                </TouchableOpacity>
              )
            }
            borderColor={colors.border as string}
            textColor={colors.text as string}
            labelColor={colors.subtext as string}
          />
          <View style={styles.upsellRow}>
            <Icon name="lock" size={12} color={colors.brand as string} strokeWidth={2} />
            <Text style={[styles.upsellText, { color: colors.subtext, marginLeft: 6 }]}>
              Zero-Cloud · Direct P2P Swarm Transfer
            </Text>
          </View>
        </View>
      )}

      {/* ── Preparing State ──────────────────────────────────────────── */}
      {isPreparing && !shareUrl && (() => {
        const fileCount = selectedFiles.length;
        const totalBytes = selectedFiles.reduce((s, f) => s + ((f as any).size ?? 0), 0);
        const totalStr = fmtSize(totalBytes);
        return (
          <View style={styles.preparingContainer}>
            <CircularProgress size={160} strokeWidth={8} progress={prepareProgress} />
            <Text style={[styles.preparingTitle, { color: colors.text }]}>Preparing transfer</Text>
            {fileCount > 0 && (
              <Text style={[styles.preparingMeta, { color: colors.subtext }]}>
                {fileCount === 1 ? 'Sending 1 file' : `Sending ${fileCount} files`}
                {totalBytes > 0 ? ` · ${totalStr} total` : ''}
              </Text>
            )}
            <TouchableOpacity
              style={[styles.cancelBtn, { borderColor: colors.border }]}
              onPress={onCloseShare}
              activeOpacity={0.8}
            >
              <Text style={[styles.cancelBtnText, { color: colors.text }]}>Cancel</Text>
            </TouchableOpacity>
          </View>
        );
      })()}

      {/* ── Result State (ShareResult molecule) ──────────────────────── */}
      {shareUrl && (
        <ShareResult
          shareUrl={shareUrl}
          shareHash={shareHash}
          torrentName={torrentName}
          torrentFile={torrentFile}
          onCopy={onCopy}
          onCloseShare={onCloseShare}
          onViewDetails={onViewDetails}
        />
      )}

      {/* ── Footer Row: Expiry Selector + Options Button ─────────────── */}
      {!isPreparing && !shareUrl && (
        <View style={styles.footerRow}>
          <TouchableOpacity
            style={[styles.expiryPill, { backgroundColor: colors.surface, borderColor: colors.border }]}
            activeOpacity={0.8}
            onPress={() => setExpiryModalVisible(true)}
          >
            <Icon name="calendar" size={14} color={colors.subtext as string} strokeWidth={2} />
            <Text style={[styles.expiryLabel, { color: colors.text }]}>
              {`Expires in ${EXPIRY_PRESETS.find(p => p.value === selectedExpiry)?.label || '3 days'}`}
            </Text>
            <Icon name="chevron-down" size={13} color={colors.subtext as string} strokeWidth={2} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.optionsBtn, { backgroundColor: colors.surface, borderColor: colors.border }]}
            activeOpacity={0.8}
          >
            <Text style={[styles.dots, { color: colors.text }]}>•••</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* ── Expiry Selection Modal ────────────────────────────────────── */}
      <Modal
        visible={expiryModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setExpiryModalVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setExpiryModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={[styles.modalContent, { backgroundColor: colors.surface, borderColor: colors.border }]}>
                <View style={[styles.modalHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.modalTitle, { color: colors.text }]}>Link expires in</Text>
                  <TouchableOpacity
                    onPress={() => setExpiryModalVisible(false)}
                    style={styles.modalCloseBtn}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <Icon name="close" size={18} color={colors.subtext as string} strokeWidth={2} />
                  </TouchableOpacity>
                </View>
                {EXPIRY_PRESETS.map((preset) => {
                  const isSelected = preset.value === selectedExpiry;
                  return (
                    <TouchableOpacity
                      key={preset.value}
                      style={[styles.modalOption, { borderBottomColor: colors.border }]}
                      onPress={() => {
                        onExpiryChange?.(preset.value);
                        setExpiryModalVisible(false);
                      }}
                      activeOpacity={0.7}
                    >
                      <Text
                        style={[
                          styles.modalOptionText,
                          {
                            color: colors.text,
                            fontWeight: isSelected ? FontWeight.bold : FontWeight.regular,
                          },
                        ]}
                      >
                        {preset.label}
                      </Text>
                      {isSelected && (
                        <Icon name="check" size={18} color={colors.brand as string} strokeWidth={2.5} />
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* ── Primary CTA Tactile Button ───────────────────────────────── */}
      {!isPreparing && !shareUrl && (
        <TouchableOpacity
          style={[
            styles.primaryBtn,
            { backgroundColor: colors.brand },
            isSending && { opacity: 0.7 },
          ]}
          onPress={async () => {
            if (isSendTab) {
              onSendFileClick();
            } else {
              if (joinCode.trim()) {
                onStartDownload();
              } else if (onPasteLink) {
                onPasteLink();
              } else {
                const text = await readFromClipboard();
                if (text) {
                  onJoinCodeChange(text.trim());
                }
              }
            }
          }}
          disabled={isSending}
          activeOpacity={0.85}
        >
          <Text style={[styles.primaryBtnLabel, { color: colors.brandText }]}>
            {isSendTab
              ? isSending
                ? 'Transferring…'
                : 'Transfer'
              : joinCode.trim()
                ? 'Download'
                : 'Paste & Download'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    width:        Layout.cardWidth,
    maxWidth:     '100%',
    borderRadius: Radius.xxl, // 24px - Iconic Floating Card Radius
    padding:      Spacing.xl, // 20px
    borderWidth:  1,
    ...Shadows.card,
  },
  uploadRow: {
    flexDirection: 'row',
    gap:           Spacing.sm,
    marginBottom:  Spacing.sm,
  },

  // Receive Quick actions
  quickActionRow: {
    flexDirection: 'row',
    gap:           Spacing.sm,
    marginBottom:  Spacing.sm,
  },
  receiveSection: {
    width: '100%',
  },
  fieldActionBtn: {
    padding: 4,
  },

  // Metadata / Badges
  upsellRow: {
    flexDirection: 'row',
    alignItems:    'center',
    marginTop:     Spacing.xs,
    marginBottom:  Spacing.xs,
  },
  upsellText: {
    fontSize:      FontSize.xs - 1,
    fontWeight:    FontWeight.medium,
    letterSpacing: -0.1,
  },

  // Form Fields
  fieldsContainer: {
    width:        '100%',
    marginBottom: Spacing.xs,
  },

  // Preparing State
  preparingContainer: {
    paddingVertical: Spacing.lg,
    alignItems:      'center',
    justifyContent:  'center',
    gap:             10,
  } as any,
  preparingTitle: {
    fontSize:      18,
    fontWeight:    FontWeight.bold,
    marginTop:     Spacing.sm,
    letterSpacing: -0.3,
  },
  preparingMeta: {
    fontSize:     FontSize.sm,
    fontWeight:   FontWeight.medium,
    marginBottom: Spacing.xs,
    fontVariant:  ['tabular-nums'] as any,
  },
  cancelBtn: {
    width:           '100%',
    paddingVertical: 12,
    borderRadius:    Radius.pill,
    borderWidth:     1,
    alignItems:      'center',
    justifyContent:  'center',
    marginTop:       Spacing.md,
  },
  cancelBtnText: {
    fontWeight: FontWeight.semibold,
    fontSize:   FontSize.sm,
  },

  // Footer Expiry
  footerRow: {
    flexDirection: 'row',
    alignItems:    'center',
    marginTop:     Spacing.md,
    marginBottom:  Spacing.md,
    gap:           Spacing.sm,
  },
  expiryPill: {
    flex:            1,
    flexDirection:   'row',
    alignItems:      'center',
    paddingHorizontal: Spacing.md,
    height:          44,
    borderRadius:    Radius.pill,
    borderWidth:     1,
    gap:             6,
  } as any,
  expiryLabel: {
    flex:          1,
    fontSize:      FontSize.xs + 1,
    fontWeight:    FontWeight.semibold,
    letterSpacing: -0.1,
  },
  optionsBtn: {
    width:          44,
    height:         44,
    borderRadius:   Radius.pill,
    borderWidth:    1,
    justifyContent: 'center',
    alignItems:     'center',
  },
  dots: {
    fontSize:      16,
    fontWeight:    FontWeight.bold,
    letterSpacing: -1,
  },

  // Primary CTA Button
  primaryBtn: {
    height:         48,
    borderRadius:   Radius.pill,
    justifyContent: 'center',
    alignItems:     'center',
    width:          '100%',
    flexDirection:  'row',
    gap:            8,
  } as any,
  primaryBtnLabel: {
    fontSize:      FontSize.sm + 1,
    fontWeight:    FontWeight.bold,
    letterSpacing: -0.2,
  },

  // Modal
  modalOverlay: {
    flex:            1,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent:  'flex-end',
  },
  modalContent: {
    borderTopLeftRadius:  Radius.xxl,
    borderTopRightRadius: Radius.xxl,
    paddingTop:           Spacing.sm,
    paddingBottom:        40,
    borderWidth:          1,
    borderBottomWidth:    0,
  },
  modalHeader: {
    flexDirection:   'row',
    alignItems:      'center',
    justifyContent:  'space-between',
    padding:         Spacing.lg,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize:      16,
    fontWeight:    FontWeight.bold,
    letterSpacing: -0.2,
  },
  modalCloseBtn: {
    padding: 4,
  },
  modalOption: {
    flexDirection:     'row',
    alignItems:        'center',
    justifyContent:    'space-between',
    paddingVertical:   16,
    paddingHorizontal: Spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  modalOptionText: {
    fontSize:      15,
    letterSpacing: -0.1,
  },
});
